import { useTranslation } from 'react-i18next';
import { useDashboard } from './useDashboard';
import { Select, Tabs, TabsProps } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { GraphTabs } from '../../../shared/Constants';
import { RegistrationGraph } from './registration-graph/RegistrationGraph';
import { ExamResults } from './exam-results/ExamResults';
import { useSnapshot } from 'valtio';
import { ExamState } from '../ExamModule.store';
import { hasPrivilege } from '../../../shared/Utils';
import { PrivilegeCodes } from '../../../shared/RoleConstants';
import useUserSession from '../../../hooks/useUserSession';
import { NoAccess } from '../../../components/NoAccess';

export const Dashboard = () => {
	const { t } = useTranslation();
	const { examsList } = useDashboard();
	const { exams } = examsList;
	const { loggedInUserPrivileges } = useUserSession();
	const examOptions = exams.map((e) => ({ value: e.id, label: e.name }));
	const examSnap = useSnapshot(ExamState);
	const examIdInSnap = examSnap.selectedExam?.id;
	const [selectedExamId, setSelectedExamId] = useState<number | undefined>(
		examIdInSnap
	);

	useEffect(() => {
		if (exams.length) {
			ExamState.selectedExam = exams[0];
			setSelectedExamId(exams[0].id);
		}
	}, [exams]);

	const handleExamChange = useCallback(
		(value: number) => {
			setSelectedExamId(value);
			const selectedExam = exams.find((exam) => exam.id === value);
			ExamState.selectedExam = selectedExam;
		},
		[exams]
	);

	const showRegistrationGraph = hasPrivilege(
		PrivilegeCodes.GET_EXAMINATION_GRAPHICAL_VIEW,
		loggedInUserPrivileges
	);

	const showResultsGraph = hasPrivilege(
		PrivilegeCodes.GET_EXAM_RESULT_GRAPHICAL_VIEW,
		loggedInUserPrivileges
	);

	const graphTabs: TabsProps['items'] = [];

	if (showRegistrationGraph) {
		graphTabs.push({
			key: GraphTabs.REGISTRATION_GRAPH,
			label: t('registrationGraph'),
			children: <RegistrationGraph />,
		});
	}

	if (showResultsGraph) {
		graphTabs.push({
			key: GraphTabs.RESULTS_GRAPH,
			label: t('resultsGraph'),
			children: <ExamResults />,
		});
	}

	if (!graphTabs.length) {
		return <NoAccess />;
	}

	return (
		<>
			<div className="flex items-center gap-2">
				<p className="text-base font-medium">{t('selectExam')}</p>
				<Select
					className="w-48"
					size="large"
					showSearch
					options={examOptions}
					filterOption={(input, option) =>
						(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
					}
					value={selectedExamId || (examOptions.length && examOptions[0].value)}
					onChange={handleExamChange}
				/>
			</div>
			<Tabs items={graphTabs} size="large" destroyInactiveTabPane={true} />
		</>
	);
};
